import { useState } from "react";
import { Link } from "react-router";
import {
  Archive,
  ArchiveRestore,
  ExternalLink,
  FolderInput,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatApiError } from "@/lib/format-api-error";
import { EditAssetDialog } from "./edit-asset-dialog";
import { MoveAssetDialog } from "./move-asset-dialog";
import type { ContentAssetRow } from "./types";
import { useDeleteAsset } from "./use-delete-asset";
import { useToggleAssetArchive } from "./use-toggle-asset-archive";

// Phase 7 sub-bucket 13. Wraps every per-row action — Open detail, Edit,
// Move, Archive/Restore, Delete. Same dropdown content shape as the legacy
// AssetRowActions; uses the new mutation hooks instead of server actions.
//
// Permission gates assume the page renders this only when the current
// session has the relevant permissions. Listed callers (list page, detail
// page header) already check `canWrite` / `canDelete` before mounting.
export function AssetRowActions({
  asset,
  variant = "ghost-icon",
}: {
  asset: ContentAssetRow;
  /**
   * `ghost-icon` — small icon button used in card/list rows.
   * `outline-pill` — a labeled "Actions" button for the detail page header.
   */
  variant?: "ghost-icon" | "outline-pill";
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const toggleArchive = useToggleAssetArchive();
  const deleteAsset = useDeleteAsset();

  const isArchived = !!asset.archived_at;
  const pending = toggleArchive.isPending || deleteAsset.isPending;

  function handleArchive() {
    toggleArchive.mutate(
      { id: asset.id, archived: !isArchived },
      {
        onSuccess: () => toast.success(isArchived ? "Restored" : "Archived"),
        onError: (err) => toast.error(formatApiError(err, "Failed to update")),
      },
    );
  }

  function handleDelete() {
    deleteAsset.mutate(asset.id, {
      onSuccess: () => {
        toast.success(asset.is_folder ? "Folder deleted" : "Asset deleted");
        setConfirmDelete(false);
      },
      onError: (err) => toast.error(formatApiError(err, "Failed to delete")),
    });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "outline-pill" ? (
            <Button variant="outline" size="sm">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Asset actions</span>
            </Button>
          ) : (
            <button
              type="button"
              aria-label="Asset actions"
              className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {!asset.is_folder && (
            <DropdownMenuItem asChild>
              <Link to={`/admin/content/${asset.id}`}>
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                Open detail
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </DropdownMenuItem>
          {!asset.is_folder && (
            <DropdownMenuItem onClick={() => setMoveOpen(true)}>
              <FolderInput className="mr-2 h-3.5 w-3.5" />
              Move to folder
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {isArchived ? (
            <DropdownMenuItem onClick={handleArchive} disabled={pending}>
              <ArchiveRestore className="mr-2 h-3.5 w-3.5" />
              Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={handleArchive} disabled={pending}>
              <Archive className="mr-2 h-3.5 w-3.5" />
              Archive
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => setConfirmDelete(true)}
            className="text-destructive focus:text-destructive"
            disabled={pending}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editOpen && (
        <EditAssetDialog asset={asset} open={editOpen} onOpenChange={setEditOpen} />
      )}

      {moveOpen && (
        <MoveAssetDialog asset={asset} open={moveOpen} onOpenChange={setMoveOpen} />
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {asset.is_folder
                ? "Delete this folder and all its contents?"
                : "Delete this asset?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {asset.is_folder
                ? "Every asset inside the folder is removed along with it. View history stays in analytics."
                : "The asset is removed from the library and any uploaded file is deleted from storage. View history is preserved for analytics."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAsset.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteAsset.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAsset.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
